<template>
  <component
    :is="linkComponentTag"
    v-focus
    v-bind="boundAttrs"
    class="sf-link"
    @click="handleClick">
    <slot />
  </component>
</template>
<script>

import { focus } from '~/directives/focus/focus';
export default {
  name: 'SfLink',
  directives: { focus },
  inheritAttrs: false,
  props: {
    link: {
      type: [String, Object],
      default: '',
    },
  },
  computed: {
    resolvedLink() {

      if (this.link) {

        return this.link;

      }

      return this.$attrs?.to ?? this.$attrs?.href ?? '';

    },
    isExternal() {

      return (typeof this.resolvedLink === 'string' && this.resolvedLink.search(/(^\/|^#)/g) === -1);

    },
    isNativeLinkTag() {

      return this.isExternal || !this.$router;

    },
    urlTag() {

      return this.isNativeLinkTag ? { href: this.resolvedLink } : { to: this.resolvedLink };

    },
    boundAttrs() {

      const attrs = { ...this.$attrs };

      // We'll call any forwarded click handler ourselves in `handleClick`
      // to avoid double-invocation and to keep navigation consistent.
      delete attrs.onClick;

      if (this.isNativeLinkTag) {

        delete attrs.to;

      } else {

        delete attrs.href;

      }

      return { ...attrs, ...this.urlTag };

    },
    linkComponentTag() {

      return this.isNativeLinkTag ? 'a' : 'NuxtLink';

    },
  },
  methods: {

    callForwardedClickHandler(event) {

      const handler = this.$attrs?.onClick;

      if (Array.isArray(handler)) {

        handler.forEach((fn) => typeof fn === 'function' && fn(event));

        return;

      }

      if (typeof handler === 'function') {

        handler(event);

      }

    },
    shouldGuardNavigation(event) {

      if (!event) {

        return false;

      }

      if (event.defaultPrevented) {

        return true;

      }

      if (event.button !== undefined && event.button !== 0) {

        return true;

      }

      if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {

        return true;

      }

      const target = event.currentTarget?.getAttribute?.('target');

      if (target === '_blank') {

        return true;

      }

      return false;

    },
    handleClick(event) {

      // Respect disabled states coming through from button-like wrappers.
      const isDisabled = Boolean(this.$attrs?.disabled) || Boolean(this.$attrs?.['aria-disabled']);

      if (isDisabled) {

        event?.preventDefault?.();
        event?.stopPropagation?.();

        return;

      }

      // Run any forwarded click handler exactly once.
      this.callForwardedClickHandler(event);

      // External/native links should behave like normal anchors.
      if (this.isNativeLinkTag) {

        return;

      }

      // If router isn't available, we can't do SPA navigation.
      if (!this.$router) {

        return;

      }

      // Mirror RouterLink guard behavior for new-tab / modifier clicks.
      if (this.shouldGuardNavigation(event)) {

        return;

      }

      // Ensure internal navigation always happens (even if NuxtLink
      // resolution/handlers are affected by dynamic rendering or attrs).
      event?.preventDefault?.();
      const to = this.resolvedLink;

      if (to) {

        this.$router.push(to).catch(() => {});

      }

    },
  },
};

</script>
